import {
  Component,
  ChangeDetectionStrategy,
  Output,
  EventEmitter,
  Inject,
  OnInit,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, Observable, of, tap } from 'rxjs';

import { ActivitySelectionVersion, StaticMessage } from '@shared/enums';
import { canDeactivateUnsavedChanges } from '@shared/guards';
import { UserPreferences } from '@shared/models';
import { BaseDialogComponent } from '@shared/models/component';

@UntilDestroy()
@Component({
  selector: 'app-user-preferences-dialog',
  templateUrl: './user-preferences-dialog.component.html',
  styleUrls: ['./user-preferences-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserPreferencesDialogComponent
  extends BaseDialogComponent
  implements OnInit
{
  @Output() updateUserPreferences = new EventEmitter<UserPreferences>();

  userPreferences: UserPreferences;
  isAppOnline: boolean;
  isHandset: boolean;

  userPreferencesForm: FormGroup = new FormGroup({
    enableFinishTimeAutoPopulationForActivities: new FormControl<
      boolean | null
    >(null),
    activitySelectionVersion: new FormControl<ActivitySelectionVersion>(null),
  });

  StaticMessage = StaticMessage;
  ActivitySelectionVersion = ActivitySelectionVersion;

  constructor(
    protected dialogRef: MatDialogRef<UserPreferencesDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      userPreferences: UserPreferences;
      isAppOnline: boolean;
      isHandset: boolean;
    }
  ) {
    super(dialogRef);
  }

  ngOnInit(): void {
    this.userPreferences = this.data.userPreferences;
    this.isAppOnline = this.data.isAppOnline;
    this.isHandset = this.data.isHandset;

    this.initForm();

    if (!this.isAppOnline) {
      this.userPreferencesForm.disable();
    } else {
      this.userPreferencesForm.enable();
    }
  }

  save(): Observable<boolean> {
    this.onDone();
    return of(false);
  }

  onDone(): void {
    this.updateUserPreferences.emit(this.userPreferencesForm.value);
    this.dialogRef?.close();
  }

  onCancel(): void {
    if (!this.userPreferencesForm.dirty) {
      this.dialogRef?.close();
      return;
    }

    canDeactivateUnsavedChanges(this, this._dialog)
      .pipe(
        untilDestroyed(this),
        filter((val) => val),
        tap(() => {
          this.dialogRef.close();
        })
      )
      .subscribe();
  }

  canDeactivate(): boolean | Observable<boolean> {
    return this.userPreferencesForm.pristine;
  }

  //#region Private methods

  private initForm(): void {
    this.userPreferencesForm.patchValue(this.userPreferences);
    this.userPreferencesForm.markAsPristine();
  }

  //#endregion
}
